import React, { useCallback, useEffect, useRef, useState } from "react"
import { AddOfferHelperText, ChangeLanguageComponent } from "components/shared/ModalHelpers"
import { Controller, useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { useDispatch, useSelector } from "react-redux"
import { GeolocationService, LocalStorageService } from "services/api-service"
import { GeolocationAutocompleteType } from "services/api-service/types"
import { SentryService } from "services/SentryService"
import { logout } from "store/auth"
import { RootState } from "store/store"
import styled from "styled-components"
import { Box, Row } from "ui/components/atoms/_atoms"
import Button from "ui/components/atoms/button"
import { InputAutocomplete } from "ui/components/atoms/input"
import Select from "ui/components/atoms/select"
import { H3, P } from "ui/components/atoms/typography"
import Modal from "ui/components/molecules/modal"
import { CATEGORY_ARRAY, LICENCES_ARRAY, OTHERPOSITIONS_ARRAY } from "ui/data"

const AddOfferBasicInfo = ({ state, onBack, onChange }: any) => {
  const { t, i18n } = useTranslation(["add-offer", "common", "data"])
  const dispatch = useDispatch()
  const { profile } = useSelector((state: RootState) => state.profile)
  const {
    getValues,
    register,
    handleSubmit,
    watch,
    setValue,
    setError,
    reset,
    control,
    formState: { errors, isValid },
  } = useForm<{
    category: string
    subcategory: string
    address: GeolocationAutocompleteType | null
  }>({
    defaultValues: {
      category: state.category,
      subcategory: state.subcategory,
      address: state.address,
    },
  })

  const timeoutId = useRef<NodeJS.Timeout | null>(null)

  const [addressOptions, setAddressOptions] = useState<GeolocationAutocompleteType[]>(
    state?.address ? [state?.address] : [],
  )

  const [addressInput, setAddressInput] = useState<string | undefined>(
    state?.address ? state?.address.name : undefined,
  )

  const [loading, setLoading] = useState<boolean>(false)

  const category = watch("category")

  const onSubmit = useCallback(
    async (el: any) => {
      onChange(el.category, el.subcategory, el.address)
    },
    [onChange],
  )

  const searchAddressAutocomplete = useCallback(
    async (
      val: string,
      onSubmit: (e: GeolocationAutocompleteType[]) => void,
      setLoading: (e: boolean) => void,
    ) => {
      try {
        setLoading(true)
        const res = await GeolocationService.autocomplete(val, "geocode", i18n.language)
        onSubmit(res)
      } catch (e) {
        SentryService.error("[ERROR GeolocationService.autocomplete]:", e)
        onSubmit([])
      } finally {
        setLoading(false)
      }
    },
    [i18n.language],
  )

  useEffect(() => {
    if (timeoutId.current) {
      clearTimeout(timeoutId.current)
    }

    timeoutId.current = setTimeout(() => {
      if (addressInput && addressInput.length > 3) {
        searchAddressAutocomplete(addressInput, setAddressOptions, setLoading)
      }
    }, 1000)
  }, [addressInput, searchAddressAutocomplete])

  const handleLogOut = useCallback(() => {
    LocalStorageService.removeObject("token")
    dispatch(logout())
  }, [dispatch])

  useEffect(() => {
    console.log(errors)
  }, [errors])

  return (
    <Modal
      show={true}
      icon="plus-circle"
      title={t("basic-info.title")}
      text={t("basic-info.text")}
      $maxWidth="500px"
      showBackground={false}
      mobileFullScreen={true}
      helperElement={
        <ChangeLanguageComponent>
          <AddOfferHelperText />
        </ChangeLanguageComponent>
      }
      helperButton={{
        text: t("logout", { ns: "common" }),
        onClick: handleLogOut,
      }}
      bottomElement={
        <Row $justifyContent="flex-end">
          <Button $type="secondary" onClick={onBack} $marginRight="S">
            {t("back", { ns: "common" })}
          </Button>
          <Button onClick={handleSubmit(onSubmit)}>{t("next", { ns: "common" })}</Button>
        </Row>
      }
    >
      <>
        <StyledGrid>
          <Select
            name="category"
            label={`${t("offer.category", { ns: "form" })}*`}
            options={CATEGORY_ARRAY.map(el => [t(el[1], { ns: "data" }), el[1]])}
            error={errors.category}
            value={watch("category")}
            rhf={register("category", {
              required: t("required", { ns: "form" }),
            })}
          />
          <Select
            name="subCategory"
            label={`${t("offer.subcategory", { ns: "form" })}*`}
            options={
              category === "drivers"
                ? LICENCES_ARRAY.map(el => [t(el[1], { ns: "data" }), el[1]])
                : OTHERPOSITIONS_ARRAY.map(el => [t(el[1], { ns: "data" }), el[1]])
            }
            disabled={!category}
            error={errors.subcategory}
            value={watch("subcategory")}
            rhf={register("subcategory", {
              required: t("required", { ns: "form" }),
            })}
          />
        </StyledGrid>
        <Box>
          <H3 $marginBottom="XXS">{t("basic-info.additional-info.title", { ns: "add-offer" })}</H3>
          <P $small $marginBottom="XS">
            {t("basic-info.additional-info.text", { ns: "add-offer" })}
          </P>
          <Controller
            name="address"
            control={control}
            rules={{
              required: t("required", { ns: "form" }),
            }}
            render={({ field }) => (
              <InputAutocomplete
                label={`${t("address", { ns: "form" })}*`}
                error={errors.address}
                inputValue={addressInput}
                onInputChange={setAddressInput}
                noOptionsText={t("not-found", { ns: "form" })}
                loading={loading}
                options={addressOptions.map(el => {
                  return {
                    label: el.name,
                    ...el,
                  }
                })}
                {...field}
              />
            )}
          />
        </Box>
        {/*<Box>*/}
        {/*  <H3 $marginBottom="XS">{t("basic-info.language.title", { ns: "add-offer" })}</H3>*/}
        {/*  <P $small>{t("basic-info.language.text", { ns: "add-offer" })}</P>*/}
        {/*  <Select*/}
        {/*    name="language"*/}
        {/*    options={locales.map(el => [*/}
        {/*      `${localeToIcon[el]} ${t(`languages.${el}`, { ns: "common" })}`,*/}
        {/*      el,*/}
        {/*    ])}*/}
        {/*    error={errors.language}*/}
        {/*    value={watch("language")}*/}
        {/*    rhf={register("language", {*/}
        {/*      required: t("required", { ns: "form" }),*/}
        {/*    })}*/}
        {/*  />*/}
        {/*</Box>*/}
      </>
    </Modal>
  )
}

export default AddOfferBasicInfo

const StyledGrid = styled(Box)`
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-gap: ${({ theme }) => theme.space.S};
  margin-bottom: ${({ theme }) => theme.space.M};

  @media (max-width: 700px) {
    grid-template-columns: 1fr;
  }
`
